import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { inventoryApi } from '@/lib/api/inventory';
import { locationsApi } from '@/lib/api/locations';
import { BarcodeDisplay } from '@/features/manifests/components/barcode-display';
import { formatToEST } from '@/lib/utils/date-format';
import { useDialog } from '@/hooks/useDialog';
import { Dialog } from '@/components/ui/dialog';
import type { InventoryItem, InventoryMovement, Location } from '@/types/inventory';

interface InventoryItemModalProps {
  itemId: number | null;
  onClose: () => void;
}

export function InventoryItemModal({ itemId, onClose }: InventoryItemModalProps) {
  const { dialog, closeDialog, showSuccess, showError } = useDialog();
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRelocate, setShowRelocate] = useState(false);
  const [relocateLocationId, setRelocateLocationId] = useState('');
  const [relocateNotes, setRelocateNotes] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const loadItemDetails = useCallback(async () => {
    if (!itemId) return;

    try {
      setIsLoading(true);
      const [itemData, movementsData] = await Promise.all([
        inventoryApi.getById(itemId),
        inventoryApi.getMovements(itemId),
      ]);
      setItem(itemData ?? null);
      setMovements(movementsData ?? []);
    } catch (error) {
      console.error('Error loading item details:', error);
      showError('Error', 'Failed to load item details');
    } finally {
      setIsLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    if (itemId) {
      loadItemDetails();
      loadLocations();
    }
  }, [itemId, loadItemDetails]);

  const loadLocations = async () => {
    try {
      const data = await locationsApi.getAll();
      setLocations(data ?? []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleRelocate = async () => {
    if (!relocateLocationId || !item) {
      showError('No Location', 'Please select a location');
      return;
    }

    try {
      await inventoryApi.relocate(
        item.id,
        parseInt(relocateLocationId),
        relocateNotes || undefined
      );
      setShowRelocate(false);
      setRelocateLocationId('');
      setRelocateNotes('');
      loadItemDetails();
      showSuccess('Success', 'Item relocated successfully');
    } catch (error) {
      console.error('Error relocating item:', error);
      showError('Error', 'Failed to relocate item');
    }
  };

  const handleAssignLocation = async () => {
    if (!relocateLocationId || !item) {
      showError('No Location', 'Please select a location');
      return;
    }

    try {
      await inventoryApi.assignLocation(item.id, parseInt(relocateLocationId));
      setShowRelocate(false);
      setRelocateLocationId('');
      setRelocateNotes('');
      loadItemDetails();
      showSuccess('Success', 'Location assigned successfully');
    } catch (error) {
      console.error('Error assigning location:', error);
      // Surface the server refusal (e.g. a flagged item — release from admin review first)
      // rather than a generic failure. The axios wrapper rejects the raw error, so the
      // message is at response.data.message.
      const message =
        (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
        'Failed to assign location';
      showError('Error', message);
    }
  };

  const handleDownloadPng = () => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg || !item) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement('a');
      link.download = `barcode-${item.barcodeValue}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const handlePrint = () => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg || !item) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = document.createElement('html');
    const head = document.createElement('head');
    const title = document.createElement('title');
    title.textContent = `Barcode: ${item.barcodeValue}`;
    const style = document.createElement('style');
    style.textContent = `
      body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        margin: 0;
        font-family: monospace;
      }
      svg {
        max-width: 100%;
      }
    `;
    head.appendChild(title);
    head.appendChild(style);

    const body = document.createElement('body');
    body.innerHTML = svgData;

    printContent.appendChild(head);
    printContent.appendChild(body);

    printWindow.document.open();
    printWindow.document.write('<!DOCTYPE html>' + printContent.outerHTML);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-900 text-green-200';
      case 'Assigned':
        return 'bg-yellow-900 text-yellow-200';
      case 'Picked':
        return 'bg-blue-900 text-blue-200';
      case 'Removed':
        return 'bg-red-900 text-red-200';
      default:
        return 'bg-gray-700 text-gray-400';
    }
  };

  if (!itemId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-gray-800 rounded-lg border border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Inventory Item Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading...</p>
          </div>
        ) : item ? (
          <div className="p-6 space-y-6">
            <div className="text-center">
              <div ref={containerRef} className="flex justify-center bg-white rounded-lg p-6 mb-4">
                <BarcodeDisplay value={item.barcodeValue} width={3} height={80} />
              </div>
              <p className="font-mono text-lg mb-4">{item.barcodeValue}</p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={handleDownloadPng}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PNG
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print
                </button>
              </div>
            </div>

            {/* Relocate Section */}
            {!showRelocate ? (
              <div className="flex justify-center">
                <button
                  onClick={() => setShowRelocate(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  {item.locationId ? 'Relocate Item' : 'Assign Location'}
                </button>
              </div>
            ) : (
              <div className="bg-gray-750 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4">
                  {item.locationId ? 'Relocate Item' : 'Assign Location'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {item.locationId ? 'New Location' : 'Location'}
                    </label>
                    <select
                      value={relocateLocationId}
                      onChange={(e) => setRelocateLocationId(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Select Location</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.code} - {loc.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  {item.locationId && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                      <textarea
                        value={relocateNotes}
                        onChange={(e) => setRelocateNotes(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                        rows={2}
                        placeholder="Reason for relocation..."
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={item.locationId ? handleRelocate : handleAssignLocation}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded transition-colors"
                    >
                      {item.locationId ? 'Relocate' : 'Assign'}
                    </button>
                    <button
                      onClick={() => {
                        setShowRelocate(false);
                        setRelocateLocationId('');
                        setRelocateNotes('');
                      }}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-750 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Manifest</label>
                  <Link to={`/manifests/${item.manifestId}`} className="text-blue-400 hover:text-blue-300 hover:underline" onClick={onClose}>
                    {item.manifestBargeName}
                  </Link>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Location</label>
                  <p className="text-white">
                    {item.locationCode || <span className="text-yellow-400">No Location</span>}
                  </p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Sort ID 1</label>
                  <p className="text-white">{item.sortId1 || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Sort ID 2</label>
                  <p className="text-white">{item.sortId2 || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Status</label>
                  <span className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(item.status)}`}>
                    {item.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Received</label>
                  <p className="text-sm text-white">
                    {formatToEST(item.receivedTimestamp)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Order</label>
                  {item.orders && item.orders.length > 0 ? (
                    <div className="space-y-1">
                      {item.orders.map((order) => (
                        <div key={order.orderId} className="flex items-center gap-2">
                          <Link to={`/orders/${order.orderId}`} className="text-blue-400 hover:text-blue-300 hover:underline" onClick={onClose}>
                            {order.orderNumber}
                          </Link>
                          <span className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(order.orderStatus)}`}>
                            {order.orderStatus}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400">-</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Created</label>
                  <p className="text-sm text-white">
                    {formatToEST(item.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-750 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">Movement History</h3>
              {movements.length === 0 ? (
                <p className="text-center text-gray-400 py-4">No movement history</p>
              ) : (
                <div className="space-y-4">
                  {movements.map((movement) => (
                    <div key={movement.id} className="flex items-start gap-3 border-l-2 border-blue-500 pl-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-blue-400">
                            {movement.fromLocationCode || 'No Location'} → {movement.toLocationCode || 'No Location'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400">
                          {movement.movedByName} · {formatToEST(movement.movedAt)}
                        </p>
                        {movement.notes && (
                          <p className="text-sm text-gray-300 mt-1 italic">{movement.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-red-400">
            Failed to load item details
          </div>
        )}

        {/* Dialog */}
        <Dialog {...dialog} onClose={closeDialog} />
      </div>
    </div>
  );
}
